"use client";

import { BillDollarIcon, CircleClockIcon, LocationPinIcon } from "icons";
import React from "react";
import { Text, ImageWrapper } from "ui-components";

import { countries } from "@/src/constants/conutries";
import { JobResponseEntity } from "@/src/hooks/api/generated";
import { capitalize, convertSnakeToCapitalCase } from "@/src/utils/string";
import { getOrganizationImages } from "@/utils/organization";

import { buildJobApplyUrl } from "../utils/buildJobApplyUrl";

import JobsDetailAction from "./jobsDetailAction";

export interface IJobOverviewCardWrapperProps {
  jobDetails: JobResponseEntity;
}

function isFiniteSalaryValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n);
}

function formatSalaryMoney(
  amount: number,
  currencyCode: string | undefined,
  fractionDigits: number
): string {
  const currency = currencyCode?.trim();
  try {
    if (currency && /^[A-Z]{3}$/.test(currency)) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(amount);
    }
    const fmt = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return currency ? `${fmt.format(amount)} ${currency}` : fmt.format(amount);
  } catch {
    const fmt = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
    return `${fmt.format(amount)}${currency ? ` ${currency}` : ""}`;
  }
}

function getOverviewSalaryText(
  job: JobResponseEntity
): JSX.Element | string | null {
  const hasFrom = isFiniteSalaryValue(job.salary_from);
  const hasTo = isFiniteSalaryValue(job.salary_to);
  if (!hasFrom && !hasTo) {
    return null;
  }

  const currency = job.salary_currency?.trim();
  const freqSuffix = job.salary_type
    ? ` ${convertSnakeToCapitalCase(job.salary_type)}`
    : "";

  if (hasFrom && hasTo) {
    const from = Number(job.salary_from);
    const to = Number(job.salary_to);
    const fractionDigits =
      Number.isInteger(from) && Number.isInteger(to) ? 0 : 2;
    const sameAmount =
      from === to || (Number.isFinite(from) && Math.abs(from - to) < 1e-9);
    if (sameAmount) {
      return `Salary: ${formatSalaryMoney(
        from,
        currency,
        fractionDigits
      )}${freqSuffix}`;
    }
    const rangePart = `${formatSalaryMoney(
      from,
      currency,
      fractionDigits
    )} – ${formatSalaryMoney(to, currency, fractionDigits)}`;
    return (
      <div className="flex gap-2">
        <BillDollarIcon className="w-[25px] h-6 inline" />
        <Text>{`Salary: ${rangePart}${freqSuffix}`}</Text>
      </div>
    );
  }

  if (hasFrom) {
    const from = Number(job.salary_from);
    const fractionDigits = Number.isInteger(from) ? 0 : 2;
    const salary = formatSalaryMoney(from, currency, fractionDigits);

    return (
      <div className="flex gap-2">
        <BillDollarIcon className="w-[25px] h-6 inline" />
        <Text>{`Salary: From ${salary}${freqSuffix}`}</Text>
      </div>
    );
  }

  const to = Number(job.salary_to);
  const fractionDigits = Number.isInteger(to) ? 0 : 2;
  const salaryUpTo = formatSalaryMoney(to, currency, fractionDigits);

  return (
    <div className="flex gap-2">
      <BillDollarIcon className="w-[25px] h-6 inline" />
      <Text>{`Salary: Up to ${salaryUpTo}${freqSuffix}`}</Text>
    </div>
  );
}

export function JobOverviewCardWrapper({
  jobDetails,
}: IJobOverviewCardWrapperProps): JSX.Element {
  const { logoImageUrl } = getOrganizationImages(
    jobDetails?.organization,
    true
  );
  const displayLogo = jobDetails?.subBrand?.profile_picture || logoImageUrl;
  const displayName =
    jobDetails?.subBrand?.name ||
    jobDetails?.sub_brand_name ||
    jobDetails?.organization?.name;

  const salaryText = getOverviewSalaryText(jobDetails);

  return (
    <div>
      <div className="relative">
        <div className="w-full">
          <div className="flex flex-col gap-0 md:gap-6">
            <div className="w-full items-center gap-4 hidden md:flex">
              {displayLogo ? (
                <div className="w-[68px] max-h-[68px]">
                  <ImageWrapper
                    src={displayLogo}
                    width={68}
                    height={68}
                    alt={`${displayName} logo image`}
                    className="rounded-[10px] w-[68px] h-[68px] border"
                    style={{
                      objectFit: "contain",
                    }}
                    isCLDImage
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <Text variant="base-bold" className="text-xl leading-normal">
                  {displayName}
                </Text>
              </div>
            </div>
            <div className="flex gap-[14px] flex-col">
              <Text className="flex gap-2">
                <LocationPinIcon className="w-6 h-6 inline" />
                <Text>
                  {countries[jobDetails?.locations?.country]} ,{" "}
                  {jobDetails?.locations?.location}
                </Text>
              </Text>
              {salaryText}
              <div className="flex justify-start gap-2">
                <CircleClockIcon className="w-6 h-6 inline" />
                {jobDetails?.type && (
                  <div className="flex justify-start items-center gap-2.5">
                    <div className="border border-black border-1 rounded-3xl px-2.5 py-1 flex items-center">
                      <Text variant="small">
                        {capitalize(jobDetails?.type)}
                      </Text>
                    </div>
                  </div>
                )}
                {jobDetails?.type_of_contract && (
                  <div className="flex justify-start items-center gap-2.5">
                    <div className="border border-black border-1 rounded-3xl px-2.5 py-1 flex items-center">
                      <Text variant="small">
                        {capitalize(jobDetails?.type_of_contract)}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <JobsDetailAction
              id={jobDetails.id}
              jobDetails={jobDetails}
              title={jobDetails.title}
              slug={jobDetails.slug}
              isApplyWithEmail={jobDetails?.organization.is_apply_via_email}
              applyEmail={jobDetails.organization.apply_email}
              applyUrl={buildJobApplyUrl(jobDetails)}
              className="w-[500px] hidden md:block"
              souceInternalId={jobDetails.source_internal_id ?? ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
